import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
  ViewChild, signal, effect
} from '@angular/core';
import {StockItem} from '../../models/stock';
import {DEFAULT_TEMPERATURE_ZONE, TemperatureZone} from '../../models/temperature-zone';
import {StockService} from '../../services/stock/stock.service';
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {faTrash, faTimes, faEdit, faSnowflake, faTemperatureLow, faHouse} from '@fortawesome/free-solid-svg-icons';
import {faClock, faCalendarAlt} from '@fortawesome/free-regular-svg-icons';
import {DatePipe} from '@angular/common';
import {Context} from '../../models/context';
import {Subject, Subscription} from 'rxjs';
import {ContextStoreService} from '../../services/context/context-store.service';
import {StockItemComponent} from "../../modals/stock-item/stock-item.component";
import {DatePickerResult, UiToastService} from "../../shared/ui";
import { AppLogger, AppLoggerService } from '../../core/logging';


@Component({
  selector: 'app-stock-items',
  templateUrl: './stock-items.component.html',
  styleUrls: ['./stock-items.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class StockItemsComponent implements OnInit, OnChanges, OnDestroy {

  private readonly log: AppLogger;

  readonly isTouchDevice = (('ontouchstart' in window) || navigator.maxTouchPoints > 0);

  @Input() productId: number;
  @Input() storeId: number;
  @Input() storeDefaultZone: TemperatureZone | null = null;
  @Input() items: StockItem[];
  @Input() quantityType: 'fixed' | 'variable' = 'fixed';
  @Input() updateTrigger: Subject<boolean> = new Subject<boolean>();

  @Output() itemsChanged = new EventEmitter();
  @Output() dragStart = new EventEmitter<{ item: StockItem; fromStoreId: number }>();
  @Output() dragEnd = new EventEmitter<void>();

  @ViewChild(StockItemComponent) stockItemModal!: StockItemComponent;

  // Icons
  faTrash = faTrash;
  faClock = faClock;
  faCalendarAlt = faCalendarAlt;
  faTimes = faTimes;
  faEdit = faEdit;

  // Temperature zone display metadata (icon + label + color per zone)
  private readonly zoneMeta: Record<TemperatureZone, { icon: IconDefinition; label: string; colorClass: string }> = {
    frozen: {icon: faSnowflake, label: 'Freezer', colorClass: 'text-info'},
    refrigerated: {icon: faTemperatureLow, label: 'Fridge', colorClass: 'text-accent'},
    ambient: {icon: faHouse, label: 'Room temperature', colorClass: 'text-warning'},
  };

  // State
  expiryEditStates = new Map<number, boolean>();
  expiryEdits = new Map<number, Date>();

  // Context
  context: Context;
  contextSubscription: Subscription;
  updateSubscription: Subscription;

  // Data
  public stockItems: StockItem[];

  loading = signal(false);       // <-- das setzt du überall (true/false)
  showLoading = signal(false);   // <-- das verwendest du im Template (verzögert)

  private loadingTimer: any = null;


  // Quantity
  private readonly DOTS_COUNT = 10;
  private readonly DOTS_COUNT_MOBILE = 5;

  constructor(
    private stockService: StockService,
    private contextStore: ContextStoreService,
    private toast: UiToastService,
    private datePipe: DatePipe,
    private appLogger: AppLoggerService
  ) {
    this.log = this.appLogger.createForClass(StockItemsComponent);

    effect(() => {
      const isLoading = this.loading();

      // wenn loading startet -> Timer setzen
      if (isLoading) {
        this.loadingTimer = setTimeout(() => {
          // nur anzeigen, wenn nach 50ms immer noch loading=true
          if (this.loading()) {
            this.showLoading.set(true);
          }
        }, 100);
        return;
      }

      // wenn loading stoppt -> Timer killen + sofort ausblenden
      if (this.loadingTimer) {
        clearTimeout(this.loadingTimer);
        this.loadingTimer = null;
      }
      this.showLoading.set(false);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['storeId'] && !changes['storeId'].firstChange) {
      this.log.debug("storeId input changed", {
        previous: changes['storeId'].previousValue,
        current: changes['storeId'].currentValue
      });
      this.loadData();
    }
  }

  ngOnInit(): void {
    this.loadData()
    this.listenToUpdates();
  }

  ngOnDestroy(): void {
    if (this.contextSubscription !== undefined) {
      this.contextSubscription.unsubscribe();
    }

    if (this.updateSubscription !== undefined) {
      this.updateSubscription.unsubscribe();
    }
  }


  loadData(): void {

    this.loading.set(true);

    if (this.items !== undefined) { // Only load data if the user has not provided items
      this.stockItems = this.items;
      this.loading.set(false);
      return;
    }

    if (this.storeId == undefined || this.storeId <= 0) {
      this.loadByProductId(this.productId); // load all items
      return;
    }

    this.loadByProductIdAndFilterByStoreId(this.productId, this.storeId)
  }

  loadByProductId(productId: number): void {
    this.loading.set(true);
    this.stockService.getStockItemsByProductId(productId).subscribe((data) => {
      this.stockItems = data;
      this.loading.set(false);
      this.log.debug('Loaded product stock', { productId, stockItems: this.stockItems });
    });
  }

  loadByProductIdAndFilterByStoreId(productId: number, storeId: number): void {
    this.loading.set(true);
    this.stockService.getStockItemsByProductIdFilteredByStoreId(productId, storeId).subscribe((data) => {
      this.stockItems = data;
      this.loading.set(false);
      this.log.debug('Loaded product stock', { productId, storeId, stockItems: this.stockItems });
    });
  }

  listenToUpdates(): void {
    this.updateSubscription = this.updateTrigger.subscribe(update => {
      if (update) {
        // Always reload when asked to refresh; do not depend on the global context store.
        this.loadData();
      }
    });
  }

  removeItemByIdFromStock(stockId: number): void {
    this.stockService.removeById(stockId).subscribe(() => {
      this.toast.success(`Removed Item (${stockId})`);
      this.onItemsChanged();
    });
  }

  closeAllEdits(): void {
    [...this.expiryEditStates.keys()].forEach((key) => {
      this.expiryEditStates.set(key, false);
    });
  }

  openExpiryEdit(itemId: number, existingDate?: Date): void {
    this.closeAllEdits();
    if (existingDate) {
      this.expiryEdits.set(itemId, new Date(existingDate));
    }
    this.expiryEditStates.set(itemId, true);
  }

  cancelExpiryEdit(itemId: number): void {
    this.expiryEditStates.set(itemId, false);
    this.expiryEdits.delete(itemId);
  }

  onDateSelected(itemId: number, result: DatePickerResult): void {
    this.log.debug('Expiry date selected', { itemId, result });
    if (result.date) {
      this.expiryEdits.set(itemId, result.date);
      this.saveExpiryEdit(itemId);
    }
  }

  saveExpiryEdit(itemId: number): void {
    const ed = this.expiryEdits.get(itemId);
    this.log.debug('Update expiry date requested', { itemId, expiryDate: ed });
    if (!ed) return;

    this.stockService.updateExpiry(itemId, ed).subscribe(_res => {
      this.log.info('Expiry updated', { itemId, expiryDate: ed });
      this.expiryEditStates.set(itemId, false);
      this.stockItems.forEach(i => {
        if (i.id === itemId) {
          i.expiry_date = ed;
          this.toast.success(`Updated expiry date to ${this.datePipe.transform(ed, 'd.MMM yyyy')}`);
        }
      });

      // Let the parent decide how broadly to refresh (e.g. all stores)
      this.onItemsChanged();
    });
  }

  isEditable(key: number): boolean {
    return this.expiryEditStates.get(key);
  }

  onItemsChanged(): void {
    if (this.items === undefined) {
      this.loadData();
    }
    this.itemsChanged.emit(true);
  }

  getQuantityLeft(item: StockItem): number {
    // falls du im Service schon parsest, kannst du hier theoretisch einfach
    // `return item.quantityLeft as number;` machen
    if (item.quantity_left == null) {
      return 0;
    }
    if (typeof item.quantity_left === 'number') {
      return item.quantity_left;
    }
    const raw = (item.quantity_left as string).trim().replace(',', '.');
    const val = parseFloat(raw);
    return isNaN(val) ? 0 : val;
  }

  getQuantityFull(item: StockItem): number {
    if (item.quantity_full == null) {
      return 0;
    }
    if (typeof item.quantity_full === 'number') {
      return item.quantity_full;
    }
    const raw = (item.quantity_full as string).trim().replace(',', '.');
    const val = parseFloat(raw);
    return isNaN(val) ? 0 : val;
  }

  private normalizeUnitType(unitType?: string): string {
    return (unitType || '').toLowerCase();
  }

  isWeightOrVolume(unitType?: string): boolean {
    const t = this.normalizeUnitType(unitType);
    return t === 'weight' || t === 'volume';
  }

  isPiece(unitType?: string): boolean {
    const t = this.normalizeUnitType(unitType);
    return t === 'piece';
  }

  isSinglePiece(item: StockItem): boolean {
    return this.isPiece(item.unit_type) && this.getQuantityFull(item) <= 1;
  }

  getUsagePercentage(item: StockItem): number {
    const left = this.getQuantityLeft(item);
    const full = this.getQuantityFull(item);

    if (!full || full <= 0) {
      return 0;
    }

    let percentage = (left / full) * 100;
    if (percentage < 0) {
      percentage = 0;
    }
    if (percentage > 100) {
      percentage = 100;
    }
    return percentage;
  }

  getDots(item: StockItem): number[] {
    return Array.from(
      {length: this.getTotalDots(item)},
      (_, i) => i
    );
  }

  getTotalDots(item: StockItem): number {
    const maxDots = window.innerWidth < 640
      ? this.DOTS_COUNT_MOBILE
      : this.DOTS_COUNT;

    const full = this.getQuantityFull(item);

    return full < maxDots ? full : maxDots;
  }

  getFilledDots(item: StockItem): number {
    const maxDots = window.innerWidth < 640
      ? this.DOTS_COUNT_MOBILE
      : this.DOTS_COUNT;

    const full = this.getQuantityFull(item);
    const left = this.getQuantityLeft(item);

    if (full <= 0) return 0;

    // Absolute mode
    if (full < maxDots) {
      return Math.max(0, Math.min(full, left));
    }

    // Percentage mode
    const percent = (left / full);
    return Math.round(percent * maxDots);
  }

  onUsageEditClicked(item: StockItem): void {
    if (!this.stockItemModal) {
      this.log.error('Stock item modal not available');
      return;
    }

    // Open the modal with the stock item
    this.stockItemModal.open(item, this.quantityType, this.storeDefaultZone).then((result) => {
      this.log.debug('Modal closed with result:', result);

      // Update the local stockItem with the new quantity
      if (result?.success && result.quantityLeft !== undefined) {
        const stockItemToUpdate = this.stockItems.find(i => i.id === item.id);
        if (stockItemToUpdate) {
          stockItemToUpdate.quantity_left = result.quantityLeft;
        }

        this.toast.success(
          `Updated stock item usage to ${result.quantityLeft.toFixed(2)} ${item.unit || ''}`
        );
      }

      this.onItemsChanged();
    }).catch((error) => {
      this.log.debug('Modal dismissed or error:', error);
    });
  }

  onRowClick(item: StockItem, event: MouseEvent): void {
    // On touch devices, we don't rely on hover or a second click – open directly
    if (this.isTouchDevice) {
      const target = event.target as HTMLElement;
      if (target.closest('button') || target.closest('.expiry-cell')) {
        return;
      }
      this.onUsageEditClicked(item);
      return;
    }

    // Desktop behavior: respect editing state and interactive elements
    if (this.isEditable(item.id)) {
      return;
    }

    const target = event.target as HTMLElement;
    if (target.closest('button') || target.closest('.expiry-cell')) {
      return;
    }

    this.onUsageEditClicked(item);
  }

  startDrag(event: DragEvent, item: StockItem): void {
    if (event.dataTransfer) {
      // Use a custom MIME type so the browser does not treat this as generic text
      event.dataTransfer.setData('application/x-stock-item-id', String(item.id));
      event.dataTransfer.effectAllowed = 'move';
    }
    this.dragStart.emit({item, fromStoreId: this.storeId});
  }

  endDrag(_event: DragEvent): void {
    this.dragEnd.emit();
  }

  private normalizeExpiryDateSource(source?: string): string {
    return (source || '').toLowerCase();
  }

  /**
   * Expiry dates coming from the backend that are not user-confirmed.
   * We only consider these sources as "imprecise".
   */
  isImpreciseExpiry(item: StockItem): boolean {
    const s = this.normalizeExpiryDateSource(item.expiry_date_source);
    return !!item.expiry_date && (s === 'default' || s === 'estimation');
  }

  impreciseExpiryLabel(item: StockItem): string {
    const s = this.normalizeExpiryDateSource(item.expiry_date_source);
    if (s === 'estimation') {
      return 'Estimated expiry date';
    }
    if (s === 'default') {
      return 'Default expiry date';
    }
    return 'Unconfirmed expiry date';
  }

  /**
   * Effective temperature zone for an item: its own zone, else the store default,
   * else the app-wide default (room temperature).
   */
  getEffectiveZone(item: StockItem): TemperatureZone {
    return item.temperature_zone ?? this.storeDefaultZone ?? DEFAULT_TEMPERATURE_ZONE;
  }

  zoneIcon(item: StockItem): IconDefinition {
    return this.zoneMeta[this.getEffectiveZone(item)].icon;
  }

  zoneLabel(item: StockItem): string {
    return this.zoneMeta[this.getEffectiveZone(item)].label;
  }

  zoneColorClass(item: StockItem): string {
    return this.zoneMeta[this.getEffectiveZone(item)].colorClass;
  }

  getDotVariantClass(item: StockItem): string {
    const pct = this.getUsagePercentage(item);
    if (pct <= 20) return 'dot-error';
    if (pct <= 50) return 'dot-warning';
    return 'dot-success';
  }
}
